import React, { useState, useEffect } from 'react';
import { Clock, Flame, TrendingUp, Percent, ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import ProductGrid from './ProductGrid'; // Import ProductGrid instead of ProductCard

const TodayDeals = ({ listings, currentUser }) => {
  const [timeLeft, setTimeLeft] = useState({ hours: 23, minutes: 59, seconds: 59 });
  const navigate = useNavigate();

  // Filter today's listings
  const todayListings = listings.filter(listing => {
    const listingDate = new Date(listing.created_at);
    const today = new Date();
  
    const isToday = listingDate.toDateString() === today.toDateString();
    const isNotCurrentUser = currentUser ? listing.seller_id !== currentUser.user_id : true;
  
    return isToday && isNotCurrentUser;
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev.seconds > 0) {
          return { ...prev, seconds: prev.seconds - 1 };
        } else if (prev.minutes > 0) {
          return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
        } else if (prev.hours > 0) {
          return { hours: prev.hours - 1, minutes: 59, seconds: 59 };
        } else {
          return { hours: 23, minutes: 59, seconds: 59 };
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  if (todayListings.length === 0) return null;

  const viewTodayDeals = () => {
    navigate('/search?today=true');
  };

  // Check which listings have discounts (for display purposes)
  const listingsWithDiscounts = todayListings.map((listing, index) => ({
    ...listing,
    hasDiscount: index < 3, // First 3 listings have discounts
    discountPercent: [25, 40, 15][index] || 0,
    originalPrice: listing.price ? parseFloat(listing.price) * 1.5 : 0
  }));

  return (
    <div className="bg-white rounded-2xl shadow-sm p-4 md:p-6 border border-gray-100 overflow-hidden">
      <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 overflow-hidden">
        <div className="flex items-center mb-4 md:mb-0">
          <div className="p-3 rounded-xl bg-gradient-to-r from-red-500 to-pink-500 text-white mr-4">
            <Flame className="w-6 h-6" />
          </div>
          <div className="overflow-hidden">
            <div className="flex items-center">
              <h2 className="text-xl font-bold text-gray-900 truncate">Today's Hot Deals</h2>
              <TrendingUp className="w-5 h-5 text-green-500 ml-2 flex-shrink-0" />
            </div>
            <p className="text-gray-600 truncate">Limited time offers ending soon</p>
          </div>
        </div>
        
        <div className="flex items-center space-x-4 overflow-hidden">
          {/* Countdown Timer */}
          <div className="bg-gradient-to-r from-red-50 to-pink-50 rounded-xl p-3 border border-red-100 overflow-hidden">
            <div className="flex items-center justify-center space-x-3">
              <Clock className="w-4 h-4 text-red-500 flex-shrink-0" />
              <div className="flex items-center space-x-1">
                <div className="text-center min-w-[35px]">
                  <div className="text-lg font-bold text-gray-900">{timeLeft.hours.toString().padStart(2, '0')}</div>
                  <div className="text-xs text-gray-500">HRS</div>
                </div>
                <div className="text-gray-400">:</div>
                <div className="text-center min-w-[35px]">
                  <div className="text-lg font-bold text-gray-900">{timeLeft.minutes.toString().padStart(2, '0')}</div>
                  <div className="text-xs text-gray-500">MIN</div>
                </div>
                <div className="text-gray-400">:</div>
                <div className="text-center min-w-[35px]">
                  <div className="text-lg font-bold text-gray-900">{timeLeft.seconds.toString().padStart(2, '0')}</div>
                  <div className="text-xs text-gray-500">SEC</div>
                </div>
              </div>
            </div>
          </div>
          
          <button
            onClick={viewTodayDeals}
            className="text-red-600 hover:text-red-700 font-medium flex items-center whitespace-nowrap"
          >
            View All
            <ChevronRight className="w-4 h-4 ml-1 flex-shrink-0" />
          </button>
        </div>
      </div>

      {/* Deal Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 overflow-hidden">
        <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 text-center border border-red-100">
          <Percent className="w-8 h-8 text-red-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-gray-900">Up to 40%</div>
          <div className="text-sm text-gray-600">Discounts</div>
        </div>
        <div className="bg-gradient-to-r from-orange-50 to-yellow-50 rounded-xl p-4 text-center border border-orange-100">
          <Flame className="w-8 h-8 text-orange-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-gray-900">{todayListings.length}</div>
          <div className="text-sm text-gray-600">Hot Deals</div>
        </div>
        <div className="bg-gradient-to-r from-yellow-50 to-green-50 rounded-xl p-4 text-center border border-yellow-100">
          <TrendingUp className="w-8 h-8 text-green-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-gray-900">87%</div>
          <div className="text-sm text-gray-600">Sell Rate</div>
        </div>
        <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 text-center border border-green-100">
          <Clock className="w-8 h-8 text-blue-500 mx-auto mb-2" />
          <div className="text-2xl font-bold text-gray-900">24h</div>
          <div className="text-sm text-gray-600">Time Left</div>
        </div>
      </div>

      {/* Today's Deals Grid using ProductGrid component */}
      <div className="relative">
        {/* Add discount badges to the first 3 listings */}
        {listingsWithDiscounts.slice(0, 3).map((listing, index) => (
          <div key={`discount-badge-${listing.listing_id}`} 
               className="absolute z-10"
               style={{ 
                 top: Math.floor(index / 2) * 15 + 10, 
                 left: (index % 2 === 0 ? 20 : (index === 1 ? '50%' : 'auto')),
                 right: (index === 2 ? 20 : 'auto'),
                 transform: index === 1 ? 'translateX(-50%)' : 'none'
               }}>
            <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-sm font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center whitespace-nowrap">
              <Percent className="w-3 h-3 mr-1 flex-shrink-0" />
              {listing.discountPercent}% OFF
            </div>
          </div>
        ))}

        {/* Today badges for all listings */}
        {listingsWithDiscounts.map((listing, index) => (
          <div key={`today-badge-${listing.listing_id}`} 
               className="absolute z-10"
               style={{ 
                 top: Math.floor(index / 4) * 15 + 10, 
                 right: (index % 4) * 25 + 20,
                 transform: 'translateY(-50%)'
               }}>
            <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center whitespace-nowrap">
              <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
              TODAY
            </div>
          </div>
        ))}

        <ProductGrid 
          listings={listingsWithDiscounts}
          viewMode="grid"
          showControls={false} // Hide controls in TodayDeals section
          hidePagination={true} // Hide pagination in TodayDeals section
          isSearchPage={false}
        />
      </div>

      {/* Alternative: Enhanced TodayDeals with custom render */}
      {false && ( /* Set to true if you want a custom enhanced view */
        <div className="space-y-4">
          {listingsWithDiscounts.map((listing, index) => (
            <div key={listing.listing_id} className="relative group">
              {/* Discount Badge (only for first 3) */}
              {listing.hasDiscount && (
                <div className="absolute -top-2 -left-2 z-20">
                  <div className="bg-gradient-to-r from-red-500 to-pink-500 text-white text-sm font-bold px-3 py-1.5 rounded-full shadow-lg flex items-center whitespace-nowrap">
                    <Percent className="w-3 h-3 mr-1 flex-shrink-0" />
                    {listing.discountPercent}% OFF
                  </div>
                </div>
              )}
              
              {/* Today Badge */}
              <div className="absolute -top-2 -right-2 z-20">
                <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white text-xs font-bold px-2 py-1 rounded-full shadow-lg flex items-center whitespace-nowrap">
                  <Clock className="w-3 h-3 mr-1 flex-shrink-0" />
                  TODAY
                </div>
              </div>

              {/* Use ProductCard directly if you need custom styling */}
              <div className="transform transition-transform hover:scale-[1.02]">
                {/* You could render ProductCard here if needed */}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Flash Sale Banner */}
      <div className="mt-8 bg-gradient-to-r from-red-500 to-pink-500 rounded-xl p-5 text-white overflow-hidden">
        <div className="absolute inset-0 opacity-10 overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white rounded-full -translate-y-16 translate-x-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white rounded-full translate-y-12 -translate-x-12"></div>
        </div>
        
        <div className="relative flex flex-col md:flex-row md:items-center justify-between overflow-hidden">
          <div className="mb-4 md:mb-0 overflow-hidden">
            <h3 className="text-xl font-bold mb-2 truncate">Flash Sale Alert! ⚡</h3>
            <p className="text-red-100 truncate">Next flash sale starts in 2 hours. Get ready for amazing discounts!</p>
          </div>
          <div className="flex items-center space-x-4 overflow-hidden">
            <div className="text-center bg-white/20 backdrop-blur-sm rounded-lg p-3 min-w-[70px]">
              <div className="text-2xl font-bold">02</div>
              <div className="text-sm opacity-90">Hours</div>
            </div>
            <div className="text-center bg-white/20 backdrop-blur-sm rounded-lg p-3 min-w-[70px]">
              <div className="text-2xl font-bold">30</div>
              <div className="text-sm opacity-90">Minutes</div>
            </div>
            <button className="px-6 py-3 bg-white text-red-600 rounded-lg font-semibold hover:bg-gray-100 whitespace-nowrap">
              Set Reminder
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TodayDeals;